import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Reveal";
import { TechStrip } from "../components/TechStrip";
import { NewsSection } from "../components/NewsSection";
import { CaseStudies } from "../components/CaseStudies";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../components/ui/carousel";
import { useLanguage } from "../i18n/LanguageContext";

const carouselArrowCls =
  "static translate-y-0 h-11 w-11 rounded-full border-[#1E3A5F]/25 bg-transparent text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-[#FBF9F5] disabled:opacity-30";

const IMG = {
  hero: "https://images.squarespace-cdn.com/content/v1/659d10446c74f52c8b4fb736/b6c33c49-c6f5-4a52-a280-26bf8208e123/5-Front+Right-ACA+Architects-AmberByOneIslans-min.jpg",
  about: "https://images.unsplash.com/photo-1743570721473-c246a742ff1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export default function Home() {
  const { t, images } = useLanguage();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <main>
      <Seo page="home" path="/" />

      {/* HERO */}
      <section ref={heroRef} className="relative pt-[72px] min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="hero-glow" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 items-center py-16 lg:py-10 relative">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="w-14 h-1.5 bg-[#B85D2A] mb-6" />
            <motion.p
              className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold mb-7"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
            >
              {t.hero.eyebrow}
            </motion.p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#142D44]">
              <MaskedLines lines={t.hero.lines} accentIndex={2} />
            </h1>
            <motion.p
              className="mt-8 text-base sm:text-lg text-[#4A5568] leading-relaxed max-w-xl"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}
            >
              {t.hero.sub}
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            >
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#1E3A5F] text-[#FBF9F5] text-sm font-medium hover:bg-[#B85D2A] transition-colors duration-300"
              >
                {t.hero.cta1}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-[#1E3A5F]/25 text-[#1E3A5F] text-sm font-medium hover:border-[#B85D2A] hover:text-[#B85D2A] transition-colors duration-300"
              >
                {t.hero.cta2}
              </Link>
            </motion.div>
            <motion.div
              className="mt-14 grid sm:grid-cols-3 gap-6 border-t border-[#E3DED4] pt-8"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.9 }}
            >
              {t.hero.markers.map((m) => (
                <div key={m.k}>
                  <p className="font-serif text-xl text-[#142D44]">{m.k}</p>
                  <p className="mt-1 text-xs text-[#718096] leading-relaxed">{m.v}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div
            className="lg:col-span-5 relative hidden lg:block"
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="absolute -top-6 -right-6 w-full h-full border border-[#B85D2A]/40 rounded-xl" />
            <div className="relative overflow-hidden rounded-xl aspect-[4/5]">
              <motion.img
                src={images.home_hero || IMG.hero}
                alt="Limestone architecture in Cyprus"
                className="w-full h-[115%] object-cover"
                style={{ y: imgY }}
              />
            </div>
            <div className="absolute bottom-5 left-5 bg-[#FBF9F5]/80 backdrop-blur-md border border-white/40 px-4 py-3 rounded-lg">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C6B4F]">Amber · Limassol, Cyprus</p>
            </div>
          </motion.div>
        </div>
      </section>

      <TechStrip />

      {/* NEEDS */}
      <section className="py-20 sm:py-24 bg-white border-b border-[#E3DED4]" data-testid="home-needs-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold">{t.needs.eyebrow}</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-[#142D44]">{t.needs.title}</h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.needs.items.map((n, i) => (
              <Reveal key={n.text} delay={i * 0.07}>
                <Link
                  to={`/services/${n.to}`}
                  data-testid={`need-card-${i}`}
                  className="group flex flex-col justify-between h-full min-h-0 sm:min-h-[150px] bg-[#EFF4F8] border border-[#D8E2EC] rounded-xl p-4 sm:p-6 lift hover:bg-[#1E3A5F]"
                >
                  <p className="text-sm sm:text-[15px] font-semibold text-[#142D44] leading-snug group-hover:text-[#FBF9F5] transition-colors duration-300">{n.text}</p>
                  <ArrowUpRight size={18} className="mt-3 sm:mt-6 text-[#B85D2A] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-20 sm:py-24" data-testid="home-about-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-5 relative order-2 lg:order-1">
            <div className="overflow-hidden rounded-xl aspect-[4/5] max-w-md">
              <img src={images.home_about || IMG.about} alt="Limestone architecture overlooking the Mediterranean sea" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="absolute -bottom-6 -right-2 lg:right-8 bg-[#1E3A5F] text-[#FBF9F5] px-6 py-5 rounded-xl max-w-[240px]">
              <p className="font-serif text-lg leading-snug">"{t.about.quote}"</p>
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold">{t.about.eyebrow}</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#142D44] leading-tight">{t.about.title}</h2>
              <p className="mt-6 text-[#4A5568] leading-relaxed">{t.about.p1}</p>
              <Link to="/about" data-testid="home-about-link" className="group mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#B85D2A]">
                {t.about.link}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 sm:py-24 bg-[#F4F1EA] border-y border-[#E3DED4]" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Carousel opts={{ align: "start" }} className="w-full">
            <div className="flex items-end justify-between gap-6">
              <Reveal className="max-w-2xl">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold">{t.services.eyebrow}</p>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#142D44] leading-tight">{t.services.title}</h2>
                <p className="mt-5 text-[#4A5568] leading-relaxed">{t.services.sub}</p>
              </Reveal>
              <div className="hidden sm:flex lg:hidden items-center gap-3 shrink-0 pb-1">
                <CarouselPrevious className={carouselArrowCls} data-testid="services-prev-button" />
                <CarouselNext className={carouselArrowCls} data-testid="services-next-button" />
              </div>
            </div>
            <Reveal>
              <CarouselContent className="mt-12 -ml-6">
                {t.services.list.map((s) => (
                  <CarouselItem key={s.id} className="pl-6 basis-[90%] sm:basis-[55%] lg:basis-1/3">
                    <Link
                      to={`/services/${s.id}`}
                      data-testid={`service-card-${s.id}`}
                      className="group flex flex-col h-full bg-white border border-[#E3DED4] rounded-2xl p-7 lift hover:border-[#1E3A5F]/40"
                    >
                      <div className="flex items-start justify-between">
                        <span className="font-mono text-sm text-[#B85D2A]">{s.number}</span>
                        <ArrowUpRight size={20} className="text-[#B85D2A] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.25} />
                      </div>
                      <h3 className="mt-5 font-serif text-2xl text-[#142D44] leading-snug group-hover:text-[#B85D2A] transition-colors duration-300">{s.title}</h3>
                      <p className="mt-3 text-xs font-mono uppercase tracking-[0.16em] text-[#8C6B4F] leading-relaxed">{s.tagline}</p>
                      <p className="mt-4 text-sm text-[#4A5568] leading-relaxed">{s.desc}</p>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Reveal>
            <div className="mt-8 flex sm:hidden items-center justify-center gap-3">
              <CarouselPrevious className={carouselArrowCls} data-testid="services-prev-button-mobile" />
              <CarouselNext className={carouselArrowCls} data-testid="services-next-button-mobile" />
            </div>
          </Carousel>
        </div>
      </section>

      <CaseStudies />

      <NewsSection />

      {/* CTA BAND */}
      <section className="py-20 sm:py-24 bg-[#F4F1EA] border-t border-[#E3DED4]" data-testid="home-cta-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#142D44]">{t.cta.title}</h2>
            <p className="mt-5 text-[#4A5568] leading-relaxed max-w-xl mx-auto">{t.cta.text}</p>
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="group mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#B85D2A] text-[#FBF9F5] text-sm font-medium hover:bg-[#1E3A5F] transition-colors duration-300"
            >
              {t.cta.btn}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
